import type { FC } from "react";
import { useSelector } from "react-redux";

import { TriggerButton, TriggerButtonGroup, TriggerButtonIntent, TriggerButtonSize } from "widgets";

import type { IAgGridCellProps } from "../cell-base-interfaces.js";
import { TableCell } from "./core/TableCell.js";
import type { User } from "@/graphql.ts";
import { isLastAdmin } from "@/modules/projects/utils/isLastAdmin.js";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { isAdminRole, isManagerRole } from "@/redux/modules/project.ts";
import type { IAppState } from "@/redux/reducer.ts";

const ProjectMemberActionCell: FC<IAgGridCellProps> = (props) => {
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);

  const { user, roleId, project } = props.data;
  const isAdmin: boolean = isAdminRole(roleId);
  const isManager: boolean = isManagerRole(roleId);
  const isTheLastAdmin: boolean = isLastAdmin(project);
  const canEditUser: boolean = project.isUserAdmin; // admins can modify the member settings

  // the admin toggle button is active if the user is not admin, or if he is admin and there are more admins around
  const adminButtonDisabledTooltip = !canEditUser
    ? "You must be an administrator to manage this role"
    : isAdmin && isTheLastAdmin
      ? `You can't toggle your admin role because there must be at least one ${getProjectLabel()} admin.`
      : null;
  // we can only set somebody to manager if he is not an admin yet (must be user)
  const managerButtonDisabledTooltip = !canEditUser
    ? "You must be an administrator to manage this role"
    : isAdmin
      ? `You can't make this user a manager because he is an admin.`
      : null;
  // count the total number of members - removing the last member is not allowed
  const memberNumber: number = project.projectMembers.length;
  // admins or the user himself can remove the user from the project, if it is not a public project and if it's not the last member
  const canRemoveUser: boolean =
    !(isTheLastAdmin && isAdmin) &&
    !(memberNumber === 1) &&
    !project.isPublic &&
    !project.isPrivateWorkspace &&
    (project.isUserAdmin || currentUser?.username === user?.username);

  return (
    <TableCell>
      <div>
        <TriggerButtonGroup>
          <TriggerButton
            label={isAdmin ? "Revoke admin role" : "Grant admin role"}
            onPress={() => props.data.onToggleAdminRole(props.data.user.id, !isAdmin)}
            size={TriggerButtonSize.Small}
            icon={"supervised_user_circle"}
            isDisabled={adminButtonDisabledTooltip}
            intent={isAdmin ? TriggerButtonIntent.Danger : TriggerButtonIntent.Secondary}
          />
          <TriggerButton
            label={isManager ? "Revoke manager role" : "Grant manager role"}
            onPress={() => props.data.onToggleManagerRole(props.data.user.id, !isManager)}
            size={TriggerButtonSize.Small}
            icon={"supervisor_account"}
            isDisabled={managerButtonDisabledTooltip}
          />
          <TriggerButton
            label={"Remove user from this project"}
            onPress={() => props.data.onDeleteMember(props.data.user)}
            size={TriggerButtonSize.Small}
            icon={"person_remove"}
            intent={TriggerButtonIntent.Danger}
            isDisabled={!canRemoveUser ? "You can't delete this user." : undefined}
          />
        </TriggerButtonGroup>
      </div>
    </TableCell>
  );
};

export { ProjectMemberActionCell };
